import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import moment from 'moment';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import AccountingWebAPIUtils from 'utils/AccountingWebAPIUtils';
import { downloadPODs } from 'utils/ShipmentWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import formatMoney from 'utils/formatters/Money';
import { isValidInvoiceForCreditMemo } from 'utils/credit-memo';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import { fetchSignedUrlByForeignKey } from '../../../../../utils/DocumentsAPIUtils';
import { sluggify } from '../../../../../utils/localization/utils';
import SendEmailModal from '../../send-email-modal';
import { BadgeAlt as Badge, Button, Card, CardHeader, CardContent, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from '@cargomatic/ui';
import { CreditCard, Download, FileText, Mail, ChevronDown, RefreshCw, Send, CircleCheck, CircleDollarSign, ExternalLink, TriangleAlert, Clock, Ban, } from 'lucide-react';
const BADGE_COLOR_MAP = {
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    red: 'border-red-200 bg-red-50 text-red-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    brand: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    gray: 'border-gray-200 bg-gray-50 text-gray-700',
};
const STATUS_ICON_MAP = {
    approved_internally: CircleCheck,
    payment_initialized: CreditCard,
    paid: CircleDollarSign,
    void: Ban,
    internal_review: Clock,
};
const getInvoiceStatusColor = (status) => {
    switch (status) {
        case 'approved_internally':
            return 'green';
        case 'payment_initialized':
            return 'blue';
        case 'paid':
            return 'brand';
        case 'void':
            return 'red';
        default:
            return 'gray';
    }
};
const InvoiceHeader = ({ invoice, invoiceId, isAdmin, isModal, canGenerateCreditMemo, disableCreditMemoOption, onOpenCreditMemoModal, onRegenerateInvoice, regenerating, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { t } = useTranslation();
    const [showSendEmailModal, setShowSendEmailModal] = useState(false);
    const [downloadingPods, setDownloadingPods] = useState(false);
    const isDebitInvoiceType = () => {
        return !(invoice === null || invoice === void 0 ? void 0 : invoice.invoice_type) || (invoice === null || invoice === void 0 ? void 0 : invoice.invoice_type) === 'debit';
    };
    const handleDownload = async () => {
        const newWindow = window.open('', '_blank');
        try {
            const { url, error } = await fetchSignedUrlByForeignKey(invoiceId);
            if (url) {
                if (newWindow) {
                    newWindow.opener = null;
                    newWindow.location.href = url;
                }
            }
            else {
                newWindow === null || newWindow === void 0 ? void 0 : newWindow.close();
                showToast(t('GET_SIGNED_URL_ERROR'), error, 'error');
            }
        }
        catch (e) {
            newWindow === null || newWindow === void 0 ? void 0 : newWindow.close();
            showToast(t('GET_SIGNED_URL_ERROR'), e.message, 'error');
        }
    };
    const sendEmail = (emails) => {
        const invoice_type = (invoice === null || invoice === void 0 ? void 0 : invoice.invoice_type) || 'debit';
        const isCreditMemo = invoice_type === 'credit';
        AccountingActionCreators.emailInvoice(invoiceId, emails, isCreditMemo);
    };
    const handlePODDownload = async () => {
        setDownloadingPods(true);
        try {
            await downloadPODs({ invoiceId: invoice._id });
        }
        catch (e) {
            showToast(e.message, null, 'error');
        }
        setDownloadingPods(false);
    };
    const sendEdiInvoice = async () => {
        var _a;
        try {
            const response = await AccountingWebAPIUtils.sendEdiInvoice(invoice._id);
            if (response.entity && ((_a = response === null || response === void 0 ? void 0 : response.entity) === null || _a === void 0 ? void 0 : _a.success)) {
                AccountingActionCreators.getInvoice(invoice._id);
            }
            else {
                showToast(t('EDI_SEND_FAILED'), null, 'error');
            }
        }
        catch (e) {
            showToast(e.message, null, 'error');
        }
    };
    const renderCreditMemoSummary = () => {
        const { total, original_invoice, currency, amount_due, due_date } = invoice;
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "flex flex-col gap-1" },
                React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('CREDIT'))),
                React.createElement("span", { className: "text-2xl font-bold text-red-500" }, formatMoney(total, currency))),
            React.createElement("div", { className: "flex flex-col gap-1" },
                React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('BALANCE'))),
                React.createElement("span", { className: "text-2xl font-bold text-emerald-600" }, formatMoney(amount_due, currency))),
            due_date && (React.createElement("div", { className: "flex flex-col gap-1" },
                React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('DUE_DATE'))),
                React.createElement("span", { className: "text-2xl font-bold" }, moment(due_date).format('LL')))),
            React.createElement("div", { className: "flex flex-col gap-1" },
                React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('CREDITED_INVOICE'))),
                React.createElement("a", { "data-testid": "a-b0a4e65d", className: "text-2xl font-bold text-primary hover:underline", href: `/invoices/${original_invoice === null || original_invoice === void 0 ? void 0 : original_invoice.id}`, target: "_blank", rel: "noreferrer" }, original_invoice === null || original_invoice === void 0 ? void 0 : original_invoice.number))));
    };
    const invoiceStatusColor = getInvoiceStatusColor(invoice.status);
    const StatusIcon = STATUS_ICON_MAP[invoice.status] || Clock;
    const pod_message = invoice.missing_pods
        ? t('WITHOUT_PODS_JOBS')
        : t('ALL_PODS_RECEIVED');
    const podBadgeColor = invoice.missing_pods ? 'red' : 'green';
    const { _id, type, order } = invoice;
    const orderId = typeof order === 'string' ? order : ((_a = order === null || order === void 0 ? void 0 : order._id) !== null && _a !== void 0 ? _a : '');
    const date_created = invoice === null || invoice === void 0 ? void 0 : invoice.date_created;
    const isMultiJobInvoice = ((_b = invoice === null || invoice === void 0 ? void 0 : invoice.jobs) === null || _b === void 0 ? void 0 : _b.length) > 1;
    const disableCMBtn = ((invoice === null || invoice === void 0 ? void 0 : invoice.credit_memos) || []).length ||
        disableCreditMemoOption ||
        isMultiJobInvoice;
    const creditMemoDisabledReason = ((invoice === null || invoice === void 0 ? void 0 : invoice.credit_memos) || []).length
        ? 'A credit memo already exists for this invoice'
        : isMultiJobInvoice
            ? 'Credit memos are not supported for multi-job invoices'
            : disableCreditMemoOption
                ? 'A credit memo was already created'
                : null;
    return (React.createElement(React.Fragment, null,
        React.createElement(Card, null,
            React.createElement(CardHeader, { className: "space-y-4" },
                React.createElement("div", { className: "flex items-start justify-between gap-4" },
                    React.createElement("div", { className: "space-y-2 min-w-0 flex-1" },
                        React.createElement("div", { className: "flex items-center gap-3 flex-wrap" },
                            React.createElement("h1", { "data-testid": "h2-0c2badfe", className: "text-3xl font-bold tracking-tight my-0" }, invoice.number),
                            invoice.missing_pods != null && (React.createElement(Badge, { variant: "outline", "data-testid": "CargomaticBadge-pod-status", className: `gap-1 ${BADGE_COLOR_MAP[podBadgeColor] || ''}` },
                                invoice.missing_pods ? (React.createElement(TriangleAlert, { className: "h-3.5 w-3.5" })) : (React.createElement(CircleCheck, { className: "h-3.5 w-3.5" })),
                                pod_message)),
                            React.createElement(Badge, { variant: "outline", "data-testid": "CargomaticBadge-invoice-status", className: `gap-1 ${BADGE_COLOR_MAP[invoiceStatusColor] || ''}` },
                                React.createElement(StatusIcon, { className: "h-3.5 w-3.5" }),
                                t(sluggify(invoice.status))),
                            isAdmin && isDebitInvoiceType() && invoice.edi_sent && (React.createElement(Badge, { variant: "outline", "data-testid": "CargomaticBadge-c98247c2", className: `gap-1 ${BADGE_COLOR_MAP.green}` },
                                React.createElement(CircleCheck, { className: "h-3.5 w-3.5" }),
                                t('EDI_SENT')))),
                        (((_c = invoice.shipper) === null || _c === void 0 ? void 0 : _c.name) ||
                            ((_e = (_d = invoice.original_invoice) === null || _d === void 0 ? void 0 : _d.shipper) === null || _e === void 0 ? void 0 : _e.name)) && (React.createElement("p", { className: "text-sm text-muted-foreground my-0" },
                            isDebitInvoiceType() ? t('INVOICE') : t('CREDIT_MEMO'),
                            " for",
                            ' ',
                            ((_f = invoice.shipper) === null || _f === void 0 ? void 0 : _f.name) ||
                                ((_h = (_g = invoice.original_invoice) === null || _g === void 0 ? void 0 : _g.shipper) === null || _h === void 0 ? void 0 : _h.name)))),
                    React.createElement(DropdownMenu, null,
                        React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Button, { variant: "outline", className: "shrink-0 gap-2" },
                                t('ACTIONS'),
                                React.createElement(ChevronDown, { className: "h-4 w-4" }))),
                        React.createElement(DropdownMenuContent, { align: "end" },
                            canGenerateCreditMemo &&
                                isValidInvoiceForCreditMemo(invoice) &&
                                (disableCMBtn ? (React.createElement(TooltipProvider, null,
                                    React.createElement(Tooltip, null,
                                        React.createElement(TooltipTrigger, { asChild: true },
                                            React.createElement("span", null,
                                                React.createElement(DropdownMenuItem, { className: "gap-2", disabled: true, onSelect: (e) => e.preventDefault() },
                                                    React.createElement(CreditCard, { className: "h-4 w-4" }),
                                                    t('CREATE_CREDIT_MEMO')))),
                                        React.createElement(TooltipContent, { side: "left" }, creditMemoDisabledReason)))) : (React.createElement(DropdownMenuItem, { className: "gap-2", onClick: onOpenCreditMemoModal },
                                    React.createElement(CreditCard, { className: "h-4 w-4" }),
                                    t('CREATE_CREDIT_MEMO')))),
                            React.createElement(AuthorizationWrapper, { excludedRegions: [REGIONS.AP] },
                                React.createElement(DropdownMenuItem, { className: "gap-2", onClick: () => setShowSendEmailModal(true) },
                                    React.createElement(Mail, { className: "h-4 w-4" }),
                                    titleize(t('EMAIL')))),
                            React.createElement(DropdownMenuItem, { className: "gap-2", onClick: handleDownload },
                                React.createElement(Download, { className: "h-4 w-4" }),
                                t('DOWNLOAD')),
                            isModal && (React.createElement(DropdownMenuItem, { className: "gap-2", onClick: () => window.open(`/invoices/${invoice._id}`, '_blank', 'noopener,noreferrer') },
                                React.createElement(ExternalLink, { className: "h-4 w-4" }),
                                t('OPEN_IN_NEW_TAB'))),
                            isDebitInvoiceType() && (React.createElement(DropdownMenuItem, { className: "gap-2", disabled: downloadingPods, onClick: () => handlePODDownload() },
                                React.createElement(FileText, { className: "h-4 w-4" }),
                                t('DOWNLOAD_PODS'))),
                            isAdmin && isDebitInvoiceType() && (React.createElement(DropdownMenuItem, { className: "gap-2", onClick: () => onRegenerateInvoice(_id, type, {
                                    orderId,
                                    invoiceDate: date_created,
                                }), disabled: regenerating },
                                React.createElement(RefreshCw, { className: "h-4 w-4" }),
                                t('REGENERATE_INVOICE'))),
                            isAdmin && isDebitInvoiceType() && !invoice.edi_sent && (React.createElement(DropdownMenuItem, { className: "gap-2", onClick: sendEdiInvoice },
                                React.createElement(Send, { className: "h-4 w-4" }),
                                t('SEND_EDI'))))))),
            React.createElement(CardContent, { className: "flex flex-wrap items-center gap-8" }, isDebitInvoiceType() ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "flex flex-col gap-1" },
                    React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('TOTAL'))),
                    React.createElement("span", { className: "text-2xl font-bold" }, formatMoney(invoice.total, invoice === null || invoice === void 0 ? void 0 : invoice.currency))),
                React.createElement("div", { className: "flex flex-col gap-1" },
                    React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('PAID'))),
                    React.createElement("span", { className: "text-2xl font-bold" }, formatMoney(invoice.total_payments, invoice === null || invoice === void 0 ? void 0 : invoice.currency))),
                React.createElement("div", { className: "flex flex-col gap-1" },
                    React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('CREDIT'))),
                    React.createElement("span", { className: "text-2xl font-bold" }, formatMoney(invoice.total_credits || 0, invoice === null || invoice === void 0 ? void 0 : invoice.currency))),
                React.createElement("div", { className: "flex flex-col gap-1" },
                    React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('BALANCE'))),
                    React.createElement("span", { className: "text-2xl font-bold text-emerald-600" }, formatMoney(invoice.amount_due, invoice === null || invoice === void 0 ? void 0 : invoice.currency))),
                invoice.due_date && (React.createElement("div", { className: "flex flex-col gap-1" },
                    React.createElement("span", { className: "text-xs font-medium text-muted-foreground" }, titleize(t('DUE_DATE'))),
                    React.createElement("span", { className: "text-2xl font-bold" }, moment(invoice.due_date).format('LL')))))) : (renderCreditMemoSummary()))),
        React.createElement(SendEmailModal, { show: showSendEmailModal, onSend: sendEmail, onHide: () => setShowSendEmailModal(false) })));
};
export default InvoiceHeader;
